import { Grid, Stack, TextField, Typography } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { CustomInputAdornment, useMinimalsTheme } from '@tom-ui/ui'
import {
  FormField,
  mustBeGreaterThan,
  notAllowPlusMinusPointSign,
  onlyAllowPositiveInt,
  required,
  useFormStore,
} from '@tom-ui/utils'
import { RailTrackResponse } from '@yard/app/api'
import { FormProps } from '@yard/app/models/form-props.model'
import { IRailTrackFormData } from '../../models/yard-setup.model'

interface Props extends FormProps {
  railTrack?: RailTrackResponse
  onSubmit: (data: IRailTrackFormData) => void
}

export const RailTrackForm = ({ id, railTrack, onSubmit }: Props) => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()
  const formStore = useFormStore<IRailTrackFormData>()

  const handleSubmit = () => {
    if (formStore.validate()) {
      onSubmit(formStore.data)
    }
  }

  return (
    <Grid
      id={id}
      component='form'
      autoComplete='off'
      noValidate
      container
      onSubmit={event => {
        event.preventDefault()
        handleSubmit()
      }}
      spacing={theme.customSpacing.m}
    >
      <Grid size={12}>
        <FormField
          formStore={formStore}
          name={'name'}
          initialValue={railTrack?.name ?? ''}
          validate={value => {
            if (!value) return t('fieldIsRequired', 'Field is required')
          }}
        >
          {store => (
            <Stack>
              <TextField
                {...store.props}
                fullWidth
                label={t('name', 'Name')}
                required
                name='name'
              />
              {store.error && (
                <Typography variant='caption' color='error'>
                  {store.error}
                </Typography>
              )}
            </Stack>
          )}
        </FormField>
      </Grid>

      <Grid size={12}>
        <FormField
          formStore={formStore}
          name={'length'}
          initialValue={railTrack?.length ?? ''}
          validate={value => {
            if (required()(value)) return t('fieldIsRequired', 'Field is required')
            if (mustBeGreaterThan(Number(value), 0))
              return t('valueMustBeGreaterThan', 'Value must be greater than {min}', { min: -1 })
          }}
        >
          {store => (
            <Stack>
              <TextField
                {...store.props}
                fullWidth
                label={t('length', 'Length')}
                onKeyDown={notAllowPlusMinusPointSign}
                onChange={e => {
                  onlyAllowPositiveInt(e)
                  store.onChange(e)
                }}
                required
                type='number'
                name='length'
                slotProps={{
                  input: {
                    endAdornment: (
                      <CustomInputAdornment position='end' color='secondary' padding={'24px'}>
                        {t('meters', 'Meters')}
                      </CustomInputAdornment>
                    ),
                  },
                }}
              />
              {store.error && (
                <Typography variant='caption' color='error'>
                  {store.error}
                </Typography>
              )}
            </Stack>
          )}
        </FormField>
      </Grid>
    </Grid>
  )
}
